import { useState, useMemo } from "react";
import { collection, addDoc, deleteDoc, doc, updateDoc, Timestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useFirestoreCollection } from "@/hooks/useFirestore";
import { Card } from "@/components/ui/Card";
import { Avatar } from "@/components/ui/Avatar";
import { EmptyState } from "@/components/ui/EmptyState";
import { SendToStoreSheet } from "@/components/features/SendToStoreSheet";
import { useTranslations } from "@/contexts/I18nContext";
import type { ShoppingListItem, MemberProfile } from "@/lib/models";

export function ShoppingPage() {
  const { householdId, household, members, profile, loading: ctxLoading } = useHouseholdContext();
  const t = useTranslations();
  // Affiliate-link CTAs (Send to Store) are adult-only per the Amazon
  // Associates Operating Agreement and family-app rules. See
  // [[activate-amazon-associates-tag-zoeyorganizer-20-ac]].
  const isAdult = profile?.memberType === "adult" || profile?.memberType === "elderly_parent";

  const { data: items, loading: itemsLoading } = useFirestoreCollection<ShoppingListItem>(
    householdId ? `households/${householdId}/shoppingListItems` : null
  );

  const [newTitle, setNewTitle] = useState("");

  const loading = ctxLoading || itemsLoading;
  const [sendStoreQuery, setSendStoreQuery] = useState<string | null>(null);

  const memberMap = useMemo(() => {
    const map: Record<string, MemberProfile> = {};
    for (const m of members) {
      if (m.uid) map[m.uid] = m;
    }
    return map;
  }, [members]);

  // Sort: uncompleted first, then by created_at descending
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      if (a.isCompleted !== b.isCompleted) return a.isCompleted ? 1 : -1;
      const aTime = a.created_at?.seconds ?? 0;
      const bTime = b.created_at?.seconds ?? 0;
      return bTime - aTime;
    });
  }, [items]);

  async function handleAdd() {
    const title = newTitle.trim();
    if (!title || !householdId || !profile?.uid) return;

    await addDoc(collection(db, `households/${householdId}/shoppingListItems`), {
      title,
      created_at: Timestamp.now(),
      created_by: profile.uid,
      household_id: householdId,
      isCompleted: false,
    });
    setNewTitle("");
  }

  async function handleToggle(item: ShoppingListItem) {
    if (!item.uid || !householdId) return;
    const ref = doc(db, `households/${householdId}/shoppingListItems`, item.uid);
    await updateDoc(ref, { isCompleted: !item.isCompleted });
  }

  async function handleDelete(item: ShoppingListItem) {
    if (!item.uid || !householdId) return;
    const ref = doc(db, `households/${householdId}/shoppingListItems`, item.uid);
    await deleteDoc(ref);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "var(--accent-color, #00C7BE)", borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  const label = household?.household_label ?? t("grocery.householdFallback");

  return (
    <div className="p-4 space-y-6">
      <h1 className="text-2xl font-bold">{t("shopping.title", { label })}</h1>

      {/* Add item */}
      <Card>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleAdd();
          }}
          className="flex gap-2"
        >
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder={t("shopping.addItem.placeholder")}
            className="flex-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
          />
          <button
            type="submit"
            disabled={!newTitle.trim()}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-[color:var(--accent-color)] disabled:opacity-40 transition-opacity"
          >
            {t("shopping.addItem.submit")}
          </button>
        </form>
      </Card>

      {/* Items */}
      {sortedItems.length === 0 ? (
        <EmptyState
          icon="🛒"
          title={t("shopping.empty")}
        />
      ) : (
        <div className="space-y-2">
          {sortedItems.map((item) => {
            const creator = memberMap[item.created_by];
            return (
              <Card key={item.uid}>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleToggle(item)}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  >
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${
                        item.isCompleted
                          ? "bg-[color:var(--accent-color)] border-[color:var(--accent-color)]"
                          : "border-gray-300"
                      }`}
                    >
                      {item.isCompleted && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={item.isCompleted ? "line-through text-gray-400" : ""}>
                        {item.title}
                      </span>
                      {item.description && (
                        <p className={`text-xs mt-0.5 ${item.isCompleted ? "text-gray-400" : "text-gray-500"}`}>
                          {item.description}
                        </p>
                      )}
                      {creator && (
                        <div className="flex items-center gap-1 mt-1">
                          <Avatar
                            src={creator.profile_url}
                            alt={creator.nickname ?? creator.name ?? ""}
                            size={16}
                          />
                          <span className="text-xs text-gray-400">
                            {t("grocery.addedBy", { name: creator.nickname ?? creator.name ?? t("grocery.addedBySomeone") })}
                          </span>
                        </div>
                      )}
                    </div>
                  </button>
                  {!item.isCompleted && isAdult && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSendStoreQuery(item.title);
                      }}
                      className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                      title={`Send "${item.title}" to a store…`}
                      aria-label={`Send "${item.title}" to a store`}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                      </svg>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleDelete(item);
                    }}
                    className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title={t("shopping.delete.title", { title: item.title })}
                    aria-label={t("shopping.delete.aria", { title: item.title })}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Bulk "Send to {store}" CTA hidden pre-launch — current UX
          decision is per-item action sheets only. */}

      {sendStoreQuery !== null && (
        <SendToStoreSheet
          query={sendStoreQuery}
          preferred={profile?.preferred_store}
          onClose={() => setSendStoreQuery(null)}
        />
      )}
    </div>
  );
}
